/**
 * inScopeRelations — pure helpers that answer "which tables are actually in
 * this query's FROM/JOIN?" so the VQE column pickers can default to those
 * tables instead of the full schema.
 *
 * Why this exists: `resolveScope` deliberately unions the query's FROM/JOIN
 * bindings with EVERY table in the SchemaSnapshot (great for IntelliSense —
 * you can reference any table by name — but wrong for the "add column" /
 * "add filter" pickers, which should only offer the tables you're querying).
 * `QueryStructure.fromItems` + `joins[].right` is the authoritative
 * in-query relation list; these helpers distil it down to the prefixes an
 * on-screen expression may use. (The pickers' own narrowing — base tables
 * only, CTEs and derived tables excluded — lives in `querySources`.)
 */

import type { FromItem, QueryStructure } from '../../../../sql-resolver/types/queryStructure';

/**
 * Folded prefixes (both the bare table name AND any alias) of every in-scope
 * from item, lower-cased. Used to decide whether a column ref like
 * `actor.first_name` points at a table that's actually in the query.
 *
 * Lower-cased deliberately (NOT the raw engine fold): `leadingTablePrefix`
 * always `.toLowerCase()`s the on-screen expression text before comparing, so
 * this set's members must agree with that direction regardless of which way
 * the engine folds identifiers. Postgres folds `'lower'` and
 * mssql/mysql/sqlite fold `'case-insensitive'` (both already lowercase, so
 * this is a no-op for them) — Oracle folds `'upper'`
 * (`identifiers.ts` `defaultFolding('oracle')`), so without this the
 * `.has(prefix)` check below never matched and every qualified ref on Oracle
 * read as out-of-scope (BUG 1). The pickers' own narrowing stays
 * engine-folded — it keys into `snapshot.columns` through `querySources`
 * (edit/edits/querySources.ts), where both sides are engine-folded.
 */
export function inScopeRelationPrefixes(
  structure: QueryStructure | null | undefined,
): Set<string> {
  const prefixes = new Set<string>();
  for (const fi of fromItemsOf(structure)) {
    const key = relationKeyOf(fi);
    if (key) prefixes.add(key.toLowerCase());
    if (fi.alias?.aliasFolded) prefixes.add(fi.alias.aliasFolded.toLowerCase());
  }
  return prefixes;
}

/**
 * The table part of a leading `[schema.]table.column` reference, folded to
 * lower case. Returns null for a bare column, a function call, `*`, or any
 * expression that doesn't open with a dotted identifier chain.
 */
export function leadingTablePrefix(expr: string): string | null {
  const m = expr.trim().match(/^([A-Za-z_][\w$]*(?:\s*\.\s*[A-Za-z_*][\w$]*)+)/);
  if (!m) return null;
  const parts = m[1].split('.').map((p) => p.trim());
  if (parts.length < 2) return null;
  // schema.table.column → table is second-to-last; table.column → first.
  return parts[parts.length - 2].toLowerCase();
}

/**
 * True when `expr` opens with a table-qualified column whose table prefix is
 * NOT among the in-scope prefixes. Bare columns are never flagged — they can't
 * be attributed to a specific table from text alone.
 */
export function isOutOfScopeColumnExpr(
  expr: string,
  inScopePrefixes: ReadonlySet<string>,
): boolean {
  const prefix = leadingTablePrefix(expr);
  if (!prefix) return false;
  return !inScopePrefixes.has(prefix);
}

/**
 * Whether a rendered SELECT item / WHERE pill expression should be flagged
 * out-of-scope (red). Adds the confidence guard on top of
 * `isOutOfScopeColumnExpr`: with no known in-scope prefixes we can't attribute
 * any ref, so nothing is flagged.
 */
export function shouldFlagOutOfScope(
  expr: string,
  inScopePrefixes: ReadonlySet<string>,
): boolean {
  if (inScopePrefixes.size === 0) return false;
  return isOutOfScopeColumnExpr(expr, inScopePrefixes);
}

function fromItemsOf(structure: QueryStructure | null | undefined): FromItem[] {
  if (!structure) return [];
  // FROM / JOIN only — not a DML statement's own target: MySQL's multi-table
  // `UPDATE a JOIN b … SET` keeps no trace of `b` in the structure, and
  // flagging a valid `b.x` would be wrong.
  const out: FromItem[] = [...(structure.fromItems ?? [])];
  for (const j of structure.joins ?? []) out.push(j.right);
  return out;
}

function relationKeyOf(fi: FromItem): string | null {
  const t = fi.target;
  if (t.kind === 'relation') return t.qualified.foldedName;
  if (t.kind === 'cte-ref') return t.cte.name.foldedName;
  return null;
}
